import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

// Click-to-toggle (not hover-only, so it behaves the same on trackpad and
// touch) header dropdown. Closes on outside click and Escape. Items render in
// a two-column grid; `footer` is an optional featured row under them.
export const NavDropdown = ({ label, isLight, children, footer = null, panelClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={`nav-dropdown-${label.toLowerCase()}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`flex items-center gap-1 rounded-full px-3.5 py-2 transition-colors ${
          isLight
            ? `hover:bg-black/[0.04] hover:text-[#1F3A2F] ${open ? "bg-black/[0.04] text-[#1F3A2F]" : ""}`
            : `hover:bg-white/[0.06] hover:text-white ${open ? "bg-white/[0.06] text-white" : ""}`
        }`}
      >
        {label}
        <ChevronDown className={`size-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute left-1/2 top-full z-50 mt-3 w-[min(600px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-2xl border animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-150 ${
            isLight
              ? "border-black/[0.06] bg-white/95 shadow-[0_24px_60px_-20px_rgba(15,33,27,0.35)] backdrop-blur-xl"
              : "border-white/10 bg-[#16332A]/95 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl"
          } ${panelClassName}`}
        >
          <div className="grid gap-1 p-2 sm:grid-cols-2">{children({ close })}</div>
          {footer && (
            <div className={`border-t p-2 ${isLight ? "border-black/[0.06] bg-[#FAFAF9]" : "border-white/10 bg-black/10"}`}>
              {footer({ close })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// One menu row: icon tile, label (+ optional badge) and a one-line description.
// disabled renders the row dimmed and non-interactive instead of hiding it.
export const NavDropdownItem = ({ isLight, icon: Icon, label, sub, onClick, testid, badge = null, disabled = false }) => (
  <button
    type="button"
    role="menuitem"
    data-testid={testid}
    disabled={disabled}
    onClick={disabled ? undefined : onClick}
    className={`group flex w-full items-start gap-3 rounded-xl p-3 text-left transition-colors ${
      disabled ? "cursor-default opacity-50" : isLight ? "hover:bg-[#F5F5F4]" : "hover:bg-white/[0.05]"
    }`}
  >
    {Icon && (
      <span
        className={`grid size-9 shrink-0 place-items-center rounded-lg transition-colors ${
          isLight ? "bg-[#FFEBD7] text-[#A67C52] group-hover:bg-[#A67C52] group-hover:text-white" : "bg-[#A67C52]/15 text-[#D4A574] group-hover:bg-[#A67C52] group-hover:text-white"
        }`}
      >
        <Icon className="size-4" />
      </span>
    )}
    <span className="min-w-0">
      <span className={`flex items-center gap-1.5 text-sm font-semibold ${isLight ? "text-[#1F3A2F]" : "text-white"}`}>
        {label}
        {badge && (
          <span className="rounded-full border border-[#A67C52]/40 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-[#A67C52]">{badge}</span>
        )}
      </span>
      {sub && <span className={`mt-0.5 block text-xs leading-5 ${isLight ? "text-[#6B8578]" : "text-[#9FB8AC]"}`}>{sub}</span>}
    </span>
  </button>
);
